/**
 * @desc
 * Sets up a navigation bridge between micro applications and the shell router.
 *
 * Micro applications trigger navigation using custom events
 * (e.g. `navigatePrescriptionSavingsDetails`).
 *
 * The shell listens to these events and resolves them to the correct route
 * using the `navigationEvent` property defined in route configuration.
 *
 * It then performs navigation using the browser history API.
 *
 * This allows micro apps to trigger navigation without needing knowledge
 * of shell routes or URLs.
 */

import {mainRoutes} from '@/router/routes';

/**
 * Build navigation state for history updates.
 */
const buildNavigationState = ({state, payload}) => {
    if (payload === null) {
        return state ?? {};
    }

    if (state && typeof state === 'object' && !Array.isArray(state)) {
        return {
            ...state,
            payload,
        };
    }

    return {payload};
};

/**
 * Extract payload from navigation event detail.
 */
const getEffectivePayload = detail => {
    const {payload, ...rest} = detail || {};

    if (payload !== null) {
        return payload;
    }

    const flatPayload = {...rest};

    return Object.keys(flatPayload).length ? flatPayload : null;
};

/**
 * Build event → route lookup table.
 */
const buildEventRouteMap = routes =>
    routes.reduce((map, route) => {
        if (route?.navigationEvent) {
            map[route.navigationEvent] = route;
        }

        return map;
    }, {});

/**
 * Resolve fallback routes (for routes that cannot define navigationEvent).
 */
const resolveFallbackRoute = (eventName, routes) => {
    if (eventName === 'navigateToHome') {
        return routes.find(route => route?.id === 'home');
    }

    return null;
};

export const setupMicroNavigationBridge = () => {
    const routes = mainRoutes;
    /**
     * Build navigation lookup once.
     */
    const eventRouteMap = buildEventRouteMap(routes);

    /**
     * Main navigation handler.
     */
    const onNavigate = evt => {
        const eventName = evt.type;

        /**
         * Handle back navigation.
         */
        if (eventName === 'navigateBack') {
            if (window.history.length > 1) {
                window.history.back();
            }

            return;
        }

        let route = eventRouteMap[eventName];

        /**
         * Handle fallback routes.
         */
        if (!route) {
            route = resolveFallbackRoute(eventName, routes);
        }

        if (!route?.path) {
            return;
        }

        const detail = evt?.detail || {};
        const payload = getEffectivePayload(detail);
        const state = detail?.state;
        const nextState = buildNavigationState({state, payload});

        window.history.pushState(nextState, '', route.path);

        /**
         * Notify React Router to re-evaluate route.
         */
        window.dispatchEvent(new PopStateEvent('popstate'));
    };

    /**
     * Register event listeners for all navigation events.
     */
    const navigationEvents = Object.keys(eventRouteMap);

    navigationEvents.forEach(event =>
        window.addEventListener(event, onNavigate)
    );

    window.addEventListener('navigateBack', onNavigate);
    window.addEventListener('navigateToHome', onNavigate);

    /**
     * Cleanup listeners when bridge is removed.
     */
    return () => {
        navigationEvents.forEach(event =>
            window.removeEventListener(event, onNavigate)
        );

        window.removeEventListener('navigateBack', onNavigate);
        window.removeEventListener('navigateToHome', onNavigate);
    };
};
